import React, { useState, useEffect, useMemo } from "react";
import { Drawer, Button, Space, Spin, message, Checkbox } from "antd";
import { useGetIsPhone } from "@extensions";
import { serverFetch } from "@extensions";
import { useUser } from "@redux";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import "./payment.css";
import { fmtDate, ftmPrice } from "extensions";

const Payment = ({ sales, open, onClose }) => {
  const isPhone = useGetIsPhone();
  const user = useUser();
  const company = useSelector((s) => s.company);
  const [paymentVids, setPaymentVids] = useState([]);
  const [loadingPaymentVids, setLoadingPaymentVids] = useState(false);
  const [selectedSales, setSelectedSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userAgreementChecked, setUserAgreementChecked] = useState(false);
  const [rulesChecked, setRulesChecked] = useState(false);

  // Таймер отсчета времени до дедлайна оплаты
  const [timerText, setTimerText] = useState("");

  useEffect(() => {
    if (open && window.self !== window.top) {
      window.parent.postMessage({ sender: "proffit", height: "800px" }, "*");
    }
  }, [open]);

  // Родитель передаёт список продаж заново на каждом рендере,
  // поэтому переинициализируем выбор только при смене состава,
  // иначе снятые галочки возвращались бы обратно
  const salesIdsKey = (sales || []).map((sale) => sale.id).join(",");

  // Инициализация выбранных записей при открытии формы
  useEffect(() => {
    if (open && sales && sales.length > 0) {
      setSelectedSales(sales.map(sale => sale.id));
    } else {
      setSelectedSales([]);
    }
    // Сбрасываем согласия при открытии формы
    setUserAgreementChecked(false);
    setRulesChecked(false);
  }, [open, salesIdsKey]);

  // Определяем, какие согласия нужны
  const needsUserAgreement = useMemo(() => {
    return !!company?.userAgreementUrl;
  }, [company]);

  const needsRules = useMemo(() => {
    return !!company?.rulesUrl;
  }, [company]);

  // Проверяем, все ли необходимые согласия даны
  const allAgreementsAccepted = useMemo(() => {
    if (needsUserAgreement && !userAgreementChecked) return false;
    if (needsRules && !rulesChecked) return false;
    return true;
  }, [needsUserAgreement, needsRules, userAgreementChecked, rulesChecked]);

  const selectedSalesData = useMemo(() => {
    return sales?.filter(sale => selectedSales.includes(sale.id)) || [];
  }, [sales, selectedSales]);

  const totalAmount = useMemo(() => {
    return selectedSalesData.reduce((sum, sale) => sum + (sale.syma || 0), 0);
  }, [selectedSalesData]);

  // Таймер отсчета времени до дедлайна оплаты (секунды запрашиваются с сервера)
  const [paymentDeadlineSeconds, setPaymentDeadlineSeconds] = useState({});

  // Истёкшие продажи: дедлайн прошёл, нельзя выбирать в корзине
  const expiredSaleIds = useMemo(() => {
    return new Set(
      sales
        .filter(sale => sale.paymentDeadline && paymentDeadlineSeconds[sale.id] === 0)
        .map(sale => sale.id)
    );
  }, [sales, paymentDeadlineSeconds]);


  useEffect(() => {
    if (open && user) {
      fetchPaymentVids();
    }
  }, [open, user]);

  useEffect(() => {
    if (!open || !sales || sales.length === 0 || !user) {
      setTimerText("");
      setPaymentDeadlineSeconds({});
      return;
    }

    // Запрашиваем оставшиеся секунды до дедлайна с сервера
    const salesWithDeadline = sales.filter((sale) => sale.paymentDeadline);
    if (salesWithDeadline.length === 0) {
      setTimerText("");
      setPaymentDeadlineSeconds({});
      return;
    }

    const idsParam = salesWithDeadline.map((sale) => `ids=${sale.id}`).join("&");
    serverFetch(`OnlineClient/SalesDeadlineSeconds?${idsParam}`, { method: "GET" }, user)
      .then((secondsMap) => {
        if (secondsMap && typeof secondsMap === "object") {
          setPaymentDeadlineSeconds(secondsMap);
        }
      })
      .catch((error) => {
        console.error("Ошибка получения секунд до дедлайна:", error);
      });
  }, [open, sales, user]);

  // Локальный обратный отсчет секунд (обновляется каждую секунду)
  useEffect(() => {
    if (!open || !user) return;
    const timer = setInterval(() => {
      setPaymentDeadlineSeconds((prev) => {
        const entries = Object.entries(prev);
        const hasPositive = entries.some(([, value]) => value > 0);
        if (!hasPositive) return prev;
        const next = {};
        entries.forEach(([key, value]) => {
          next[key] = value > 0 ? value - 1 : value;
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [open, user]);

  // Отображаем таймер: минуты и секунды
  useEffect(() => {
    if (!paymentDeadlineSeconds || Object.keys(paymentDeadlineSeconds).length === 0) {
      setTimerText("");
      return;
    }

    const secondsList = Object.values(paymentDeadlineSeconds).filter((s) => s != null);
    if (secondsList.length === 0) {
      setTimerText("");
      return;
    }

    const minSeconds = Math.min(...secondsList);

    if (minSeconds <= 0) {
      setTimerText("Срок оплаты истёк");
    } else {
      const minutes = Math.floor(minSeconds / 60);
      const remainingSeconds = minSeconds % 60;
      setTimerText(`Ссылка на оплату активна: ${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`);
    }
  }, [paymentDeadlineSeconds]);

  const fetchPaymentVids = async () => {
    if (!user) return;

    setLoadingPaymentVids(true);
    try {
      const data = await serverFetch("OnlineClient/PaymentVids", { method: "GET" }, user);
      setPaymentVids(data || []);
    } catch (error) {
      console.error("Ошибка получения видов оплат:", error);
      message.error(error?.UserMessage || "Не удалось загрузить виды оплат");
      setPaymentVids([]);
    } finally {
      setLoadingPaymentVids(false);
    }
  };

  const handlePay = async (vid) => {
    if (!user || selectedSales.length === 0) {
      return;
    }

    setLoading(true);
    try {
      const queryParams = {
        ids: selectedSales,
        vid: vid,
      };

      const result = await serverFetch("OnlineClient/Pay", { method: "GET", queryParams: queryParams }, user);
      
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error("Ошибка при оплате:", error);
      message.error(error.userMessage || "Не удалось выполнить оплату");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer title="Оплата" placement="right" onClose={onClose} open={open} width={isPhone ? "100%" : 540} closable={true} className={`payment-drawer ${isPhone ? "payment-drawer-mobile" : ""}`}>
      <div className="payment-content">
        {timerText && <div className="payment-timer">{timerText}</div>}
        {sales && sales.length > 0 ? (
          <>
            <div className="payment-list">
              {sales.map((sale) => (
                <div key={sale.id} className="payment-item">
                  <Checkbox
                    checked={selectedSales.includes(sale.id)}
                    disabled={expiredSaleIds.has(sale.id)}
                    onChange={(e) => {
                      if (expiredSaleIds.has(sale.id)) return;
                      if (e.target.checked) {
                        setSelectedSales([...selectedSales, sale.id]);
                      } else {
                        setSelectedSales(selectedSales.filter(id => id !== sale.id));
                      }
                    }}
                    className="payment-item-checkbox"
                  />
                  <div className="payment-item-info">
                    <div className="payment-item-date">{fmtDate(sale.docDate)}</div>
                    {sale.productName && (<div className="payment-item-product">{sale.productName}</div>)}
                    {expiredSaleIds.has(sale.id) && <div className="payment-item-expired">Срок оплаты истёк</div>}
                  </div>
                  <div className="payment-item-price">{ftmPrice(sale.syma)} руб.</div>
                </div>
              ))}
            </div>
            <div className="payment-total">
              <div className="payment-total-label">Общая сумма:</div>
              <div className="payment-total-amount">{ftmPrice(totalAmount)} руб.</div>
            </div>
            {(needsUserAgreement || needsRules) && (
              <div className="payment-agreements">
                {needsUserAgreement && (
                  <Checkbox
                    checked={userAgreementChecked}
                    onChange={(e) => setUserAgreementChecked(e.target.checked)}
                    className="payment-agreement-checkbox"
                  >
                    Я согласен с{" "}
                    <a href={company.userAgreementUrl} target="_blank" rel="noreferrer">
                      Пользовательским соглашением
                    </a>
                  </Checkbox>
                )}
                {needsRules && (
                  <Checkbox
                    checked={rulesChecked}
                    onChange={(e) => setRulesChecked(e.target.checked)}
                    className="payment-agreement-checkbox"
                  >
                    Я согласен с{" "}
                    <a href={company.rulesUrl} target="_blank" rel="noreferrer">
                      Условиями и правилами
                    </a>
                  </Checkbox>
                )}
              </div>
            )}
            {loadingPaymentVids ? (
              <div className="payment-vids-loading"><Spin /></div>
            ) : (
              <div className="payment-vids">
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  {paymentVids.map((vid) => (
                    <Button
                      key={vid.id}
                      type="primary"
                      block
                      className="payment-vid-button"
                      disabled={totalAmount === 0 || loading || !allAgreementsAccepted}
                      loading={loading}
                      onClick={() => handlePay(vid.id)}
                    >
                      {vid.name}
                    </Button>
                  ))}
                </Space>
              </div>
            )}
          </>
        ) : (
          <div className="payment-empty">Нет товаров для оплаты</div>
        )}
      </div>
    </Drawer>
  );
};

export default Payment;

